import React from 'react';
import * as yup from 'yup';
import { useSparkBuilderStore } from 'stores';
import { Avatar, Box, Divider } from '@mui/material';
import { InfoCard, SparkAccordion } from 'sharedComponents/UI';
import { SparkForm, SparkFormButton } from 'sharedComponents/SparkForm';
import { SuccessStatusIcon, WarningStatusIcon } from 'components/SVG';
import TypeDetailCard from 'components/CreateSpark/Modals/SelectStepTypeModal/TypeDetailCard';
import PromptAccordion from '../PromptAccordion';
import TestAccordion from '../TestAccordion';
import { useDialog } from 'hooks/common';
import { useMemoryStatusTypes } from 'hooks/EntityTypes/useMemoryStatusTypes';
import { parseJSONCleaned } from 'utilities/index';
import type { StepTest } from 'types/SparkStep';
import type { JoinedMemory } from 'types/Memory';

type MemoryFormProps = {
  selectedMemory: JoinedMemory;
  handleTestClick: () => void;
  openCreateMemoryDialog: () => void;
  promptFunctionID: string;
};

function MemoryForm({
  selectedMemory,
  handleTestClick,
  promptFunctionID,
}: MemoryFormProps) {
  const selectedStep = useSparkBuilderStore(
    (store) => store.builderSideBar.selectedStep
  );

  const { idMap } = useMemoryStatusTypes();

  const currentSparkStep = useSparkBuilderStore((store) =>
    store.sparkStepsActions.getSparkStep(selectedStep?.id || '')
  );
  const sparkTestResults = parseJSONCleaned<StepTest>(
    currentSparkStep?.attributes.testResults || ''
  );
  const isOutputAvailable = !!sparkTestResults?.SparkTask.Output;

  const stepMetaDiagnostics = currentSparkStep?.meta.diagnostics;
  const currentMemoryStatus =
    idMap?.[selectedMemory.attributes.memoryStatusTypeID];

  // Memories are not being active due to some backend reason
  // const isMemoryActive = currentMemoryStatus?.attributes.name === 'Published';
  const isMemoryActive = true;

  const memoryDialogConfig = useDialog(!isMemoryActive && !isOutputAvailable);
  const promptDialogConfig = useDialog(isMemoryActive);
  const testDialogConfig = useDialog(isOutputAvailable);

  const handleMemoryOpen = () => {
    memoryDialogConfig.openDialog();
    testDialogConfig.closeDialog();
    promptDialogConfig.closeDialog();
  };
  const handlePromptAccordionOpen = () => {
    memoryDialogConfig.closeDialog();
    testDialogConfig.closeDialog();
    promptDialogConfig.openDialog();
  };
  const handleTestOpen = () => {
    memoryDialogConfig.closeDialog();
    testDialogConfig.openDialog();
    promptDialogConfig.closeDialog();
  };
  const _handleSubmit = async () => {
    handleTestOpen();
  };
  
  const memoryAttachments = {
    id: selectedMemory.id,
    title: selectedMemory?.attributes.name || '',
    type: 'memory',
    details: selectedMemory?.attributes.name || 'Memories',
    fileTypeTag: selectedMemory.type,
  };

  return (
    <>
      <Box width='100%' key={selectedMemory.id}>
        <PromptAccordion
          dialogConfig={{
            ...promptDialogConfig,
            openDialog: handlePromptAccordionOpen,
          }}
          onAfterSubmit={_handleSubmit}
          promptFunctionID={promptFunctionID}
          memoryAttachments={[memoryAttachments]}
          hasIssues={!isMemoryActive}
        />
        <Box width='100%'>
          <Divider />
        </Box>
        {isMemoryActive && !stepMetaDiagnostics?.hasMemoryQueryIssues && (
          <TestAccordion
            dialogConfig={{ ...testDialogConfig, openDialog: handleTestOpen }}
            hasTestIssues={!!stepMetaDiagnostics?.hasTestIssues}
            handleContinue={handleTestClick}
            sparkTestResults={sparkTestResults}
          />
        )}
      </Box>
    </>
  );
}

export default MemoryForm;
